import type { CSSProperties, FC, HTMLAttributes, ReactNode } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

interface HierarchyTreeRowProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
    anchorOffsetRem?: number;
    ancestorRailDepths?: readonly number[];
    ancestorRailOverlapRem?: number;
    children: ReactNode;
    continueToChildren?: boolean;
    continueToSiblings?: boolean;
    contentAvatarInsetRem?: number;
    contentStyle?: CSSProperties;
    connectorOverlapPx?: number;
    depth: number;
    firstChildRailStartOffsetRem?: number;
    firstSibling?: boolean;
    indentRem?: number;
    railColor?: CSSProperties['color'];
    rootRailInsetRem?: number;
    rowCenterRem?: number;
    siblingRailStartOffsetRem?: number;
}

/**
 * Indents hierarchical row content while drawing a continuous, avatar-aligned
 * file-tree rail outside of the interactive row surface.
 */
const HierarchyTreeRow: FC<HierarchyTreeRowProps> = ({
    anchorOffsetRem,
    ancestorRailDepths = [],
    ancestorRailOverlapRem = 0,
    children,
    continueToChildren = false,
    continueToSiblings = false,
    contentAvatarInsetRem = 0.75,
    contentStyle,
    connectorOverlapPx = 1,
    depth,
    firstChildRailStartOffsetRem,
    firstSibling = true,
    indentRem = 1.75,
    railColor,
    rootRailInsetRem = 1.375,
    rowCenterRem = 1.25,
    siblingRailStartOffsetRem,
    style,
    ...props
}) => {
    const { token } = useAntDesignTheme();
    const connectorTopOffset = firstSibling
        ? (firstChildRailStartOffsetRem ?? rowCenterRem / 2)
        : (siblingRailStartOffsetRem ?? rowCenterRem);
    const railInset = (depth - 1) * indentRem + rootRailInsetRem;
    const resolvedRailColor = railColor ?? token.colorBorder;
    const indent = depth * indentRem;
    const anchor = anchorOffsetRem ?? rowCenterRem;

    return (
        <div {...props} data-hierarchy-tree-depth={depth} style={{ minWidth: 0, position: 'relative', ...style }}>
            {depth > 0 ? (
                <>
                    {ancestorRailDepths.map((ancestorDepth) => (
                        <span
                            aria-hidden
                            data-hierarchy-tree-rail='true'
                            key={ancestorDepth}
                            style={{
                                borderInlineStart: `1px solid ${resolvedRailColor}`,
                                bottom: 0,
                                insetInlineStart: `${ancestorDepth * indentRem + rootRailInsetRem}rem`,
                                position: 'absolute',
                                top: `-${ancestorRailOverlapRem}rem`,
                            }}
                        />
                    ))}
                    {continueToSiblings ? (
                        <span
                            aria-hidden
                            data-hierarchy-tree-sibling-continuation='true'
                            style={{
                                borderInlineStart: `1px solid ${resolvedRailColor}`,
                                bottom: 0,
                                insetInlineStart: `${railInset}rem`,
                                position: 'absolute',
                                top: `${anchor}rem`,
                            }}
                        />
                    ) : null}
                    <span
                        aria-hidden
                        data-hierarchy-tree-elbow='true'
                        style={{
                            borderBottom: `1px solid ${resolvedRailColor}`,
                            borderInlineStart: `1px solid ${resolvedRailColor}`,
                            borderRadius: `0 0 0 ${token.borderRadiusSM}px`,
                            height:
                                anchorOffsetRem === undefined
                                    ? `calc(50% + ${connectorTopOffset}rem)`
                                    : `${anchor + connectorTopOffset}rem`,
                            insetInlineStart: `${railInset}rem`,
                            position: 'absolute',
                            top: `-${connectorTopOffset}rem`,
                            width: `calc(${indentRem + contentAvatarInsetRem - rootRailInsetRem}rem + ${connectorOverlapPx}px)`,
                        }}
                    />
                </>
            ) : null}
            {continueToChildren ? (
                <span
                    aria-hidden
                    data-hierarchy-tree-child-continuation='true'
                    style={{
                        borderInlineStart: `1px solid ${resolvedRailColor}`,
                        bottom: 0,
                        insetInlineStart: `${indent + rootRailInsetRem}rem`,
                        position: 'absolute',
                        top: `${anchor}rem`,
                    }}
                />
            ) : null}
            <div
                data-hierarchy-tree-content='true'
                style={{
                    marginInlineStart: indent ? `${indent}rem` : undefined,
                    minWidth: 0,
                    position: 'relative',
                    width: indent ? `calc(100% - ${indent}rem)` : '100%',
                    zIndex: 1,
                    ...contentStyle,
                }}
            >
                {children}
            </div>
        </div>
    );
};

export default HierarchyTreeRow;
